import React,{useState} from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import BulkConfig from './BulkConfig';
import ThumbnailUrlInput from './ThumbnailUrlInput';
import UrlContextInput from './UrlContextInput';
import VideoUrlInput from './VideoUrlInput';
import CanonicalBaseUrlInput from './CanonicalBaseUrlInput';
import ScriptUrlInput from './ScriptUrlInput';
import ModelTester from './ModelTester';
import AnthropicModelPicker from './AnthropicModelPicker';
import {countAltTexts,detectTemplateBrand,sanitiseKeyword} from '../utils/keywordReplacer';
import {slugify} from '../utils/random';
const {FiUploadCloud,FiChevronDown,FiEye,FiEyeOff,FiPackage,FiZap,FiAlertCircle,FiCheckCircle,FiAlertTriangle,FiServer,}=FiIcons;

const InputField=({label,required,hint,...props})=> (
  <div>
    <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
      {label}{required && <span className="text-indigo-400 ml-1">*</span>}
    </label>
    <input required={required} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-slate-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all" {...props} />
    {hint && <p className="text-xs text-slate-600 mt-1">{hint}</p>}
  </div>
);

const validateKey=(key)=> {
  const k=(key || '').trim();
  if (!k) return null;
  if (!k.startsWith('sk-ant-')) return 'Key must start with "sk-ant-"';
  if (k.length < 40) return `Key too short (${k.length} chars) — copy the full key`;
  return 'valid';
};

const GeneratorForm=({onSubmit,isGenerating})=> {
  const [formData,setFormData]=useState({
    keyword: '',
    altKeyword: '',
    oldNames: '',
    siteName: '',
    contentStyle: 'description',
    product: '',
    seed: '',
    model: 'claude-3-haiku-20240307',
    apiKey: '',
    testNotes: '',
    metricNotes: '',
    evidenceUrls: '',
    verifiedVideoUrl: '',
    imageEvidence: '',
    videoNotes: '',
    docExcerpts: '',
    affiliateDisclosure: false,
    researchReviews: true,
  });
  const [customImageBase,setCustomImageBase]=useState('');
  const [customVideoBase,setCustomVideoBase]=useState('');
  const [contextUrl,setContextUrl]=useState('');
  const [canonicalBase,setCanonicalBase]=useState('');
  const [scriptBase,setScriptBase]=useState('');
  const [proxyUrl,setProxyUrl]=useState('');
  const [file,setFile]=useState(null);
  const [dragOver,setDragOver]=useState(false);
  const [showKey,setShowKey]=useState(false);
  const [bulkCount,setBulkCount]=useState(10);
  const [showTester,setShowTester]=useState(false);
  const [templateScan,setTemplateScan]=useState(null);
  const [oldNamesEdited,setOldNamesEdited]=useState(false);

  const productSlug=(formData.product || '').toLowerCase().replace(/[^a-z0-9]/g,'');
  const keyStatus=validateKey(formData.apiKey);

  const handleChange=(e)=> {
    const {name,type,checked}=e.target;
    const value=type==='checkbox' ? checked : e.target.value;
    if (name==='oldNames') setOldNamesEdited(true);
    setFormData((prev)=> ({...prev,[name]: value}));
  };

  // Scan the uploaded template for its old brand name and alt texts so the
  // "Old Name(s) in Template" field can be pre-filled.
  const handleFile=(f)=> {
    if (!f) return;
    setFile(f);
    f.text().then((text)=> {
      const detected=detectTemplateBrand(text);
      setTemplateScan({detected,altCount: countAltTexts(text)});
      if (detected && !oldNamesEdited) setFormData((prev)=> ({...prev,oldNames: detected}));
    }).catch(()=> setTemplateScan(null));
  };
  const handleDrop=(e)=> {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const isValidUrl=(str)=> {
    if (!str) return true;
    try { new URL(str); return true; } catch { return false; }
  };

  const handleSelectModel=(modelId)=> {
    setFormData((prev)=> ({...prev,model: modelId}));
    setShowTester(false);
  };

  const handleSubmit=(e)=> {
    e.preventDefault();
    if (!file) return alert('Please upload a template file first.');
    const trimmedKey=(formData.apiKey || '').trim();
    if (!trimmedKey) return alert('Please enter your Anthropic API key.');
    if (!trimmedKey.startsWith('sk-ant-')) {
      return alert(`Invalid API key format.\n\nMust start with "sk-ant-".\nGot: "${trimmedKey.slice(0,15)}..."\n\nCopy your key from console.anthropic.com.`);
    }
    if (trimmedKey.length < 40) {
      return alert(`API key appears incomplete (${trimmedKey.length} chars).\nCopy the full key from console.anthropic.com.`);
    }
    if (customImageBase && !isValidUrl(customImageBase)) return alert('Custom Thumbnail URL is not valid.');
    if (customVideoBase && !isValidUrl(customVideoBase)) return alert('Custom Video URL is not valid.');
    if (contextUrl && !isValidUrl(contextUrl)) return alert('Source URL is not valid.');
    if (!canonicalBase.trim()) return alert('Please enter the Canonical Base URL: the address where you will upload these pages (e.g. https://yoursite.com/reviews/).');
    if (!isValidUrl(canonicalBase)) return alert('Canonical Base URL is not valid.');
    if (scriptBase && !isValidUrl(scriptBase)) return alert('Script Base URL is not valid.');
    if (proxyUrl && !isValidUrl(proxyUrl)) return alert('Anthropic Proxy URL is not valid.');
    if (formData.verifiedVideoUrl && !/^https?:\/\//i.test(formData.verifiedVideoUrl)) return alert('Provide an http(s) URL to an actual recording.');

    const altKeyword=sanitiseKeyword(formData.altKeyword);
    if (!altKeyword) return alert('Please enter a Keyword (used for image alt text and to replace the old product name).');

    const params={
      ...formData,
      altKeyword,
      apiKey: trimmedKey,
      provider: 'anthropic',
      seed: formData.seed !=='' ? parseInt(formData.seed,10) : undefined,
      customImageBase: customImageBase.trim() || null,
      customVideoBase: customVideoBase.trim() || null,
      contextUrl: contextUrl.trim() || null,
      canonicalBase: canonicalBase.trim() || null,
      scriptBase: scriptBase.trim() || null,
      proxyUrl: proxyUrl.trim() || null,
    };
    onSubmit(params,file,bulkCount);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-800 flex items-center gap-3">
        <SafeIcon icon={FiPackage} className="text-indigo-400 text-lg" />
        <h2 className="text-base font-semibold text-white">Bulk Generator Configuration</h2>
        <div className="ml-auto flex items-center gap-1.5 px-2 py-1 bg-green-950/40 border border-green-800/40 rounded-full">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
          <span className="text-xs text-green-400 font-mono">No CORS issues</span>
        </div>
      </div>

      <div className="p-6 space-y-4">
        <InputField label="Target Keyword (SEO Title)" required type="text" name="keyword" value={formData.keyword} onChange={handleChange} placeholder="e.g. Mitolyn Reviews & Complaints" />
        <div className="grid grid-cols-2 gap-4">
          <InputField
            label="Keyword"
            required
            type="text"
            name="altKeyword"
            value={formData.altKeyword}
            onChange={handleChange}
            placeholder="e.g. SEO NEO"
            hint={`Brand name shown on the page: image alt text, byline, Product, copyright, and anywhere the old name appeared. File names: ${formData.altKeyword.trim() ? slugify(formData.altKeyword) : 'keyword'}-reviews-complaints-6gsua5.html`}
          />
          <InputField
            label="Old Name(s) in Template"
            type="text"
            name="oldNames"
            value={formData.oldNames}
            onChange={handleChange}
            placeholder="e.g. NerveCell, Nerve Cell"
            hint={templateScan
              ? `${templateScan.detected ? `Detected "${templateScan.detected}" in template` : 'No old name detected'} · ${templateScan.altCount} image alt texts. Separate multiple names with commas.`
              : 'Auto-detected when you upload a template. Separate multiple names with commas.'}
          />
        </div>
        <InputField
          label="Site Name"
          type="text"
          name="siteName"
          value={formData.siteName}
          onChange={handleChange}
          placeholder={`e.g. ${formData.altKeyword.trim() || 'SEO NEO'} Reviews`}
          hint={`Optional. The site name shown in the page header, byline, footer and schema publisher (video-review template). Replaces the template's own site name. Leave blank to use "${formData.altKeyword.trim() || 'Keyword'} Reviews".`}
        />
        <div className="grid grid-cols-2 gap-4">
          <InputField label="JS Redirect File Name" type="text" name="product" value={formData.product} onChange={handleChange} placeholder="e.g. Mitolyn" />
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Base Seed</label>
            <input type="number" name="seed" value={formData.seed} onChange={handleChange} placeholder="(optional)" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-slate-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all" />
          </div>
        </div>

        <div className="p-4 bg-slate-950/60 rounded-lg border border-slate-800 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <SafeIcon icon={FiZap} className="text-indigo-400 text-sm" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Anthropic Claude</span>
            <span className="ml-auto text-xs px-2 py-0.5 bg-green-950/40 text-green-400 border border-green-800/40 rounded-full font-mono">Direct · No Proxy</span>
          </div>

          <div className="flex items-start gap-2 px-3 py-2 bg-yellow-950/20 border border-yellow-800/30 rounded-lg">
            <SafeIcon icon={FiAlertTriangle} className="text-yellow-400 text-xs shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-yellow-300 font-semibold">Getting 404 "model not found" errors?</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Model availability depends on your API key tier. Use the tester below to find which models work for your key. Start with <strong className="text-white">Claude Haiku 3</strong> — it has the broadest availability.
              </p>
            </div>
          </div>

          <AnthropicModelPicker
            apiKey={formData.apiKey}
            proxyUrl={proxyUrl}
            value={formData.model}
            onChange={handleSelectModel}
          />

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
              Anthropic API Key<span className="text-indigo-400 ml-1">*</span>
            </label>
            <div className="relative">
              <input required type={showKey ? 'text' : 'password'} name="apiKey" value={formData.apiKey} onChange={handleChange} placeholder="sk-ant-api03-..." className={`w-full bg-slate-900 border rounded-lg px-4 py-2.5 text-white text-sm placeholder-slate-600 focus:ring-2 focus:border-transparent outline-none transition-all pr-20 ${keyStatus==='valid' ? 'border-green-600/60 focus:ring-green-500' : keyStatus ? 'border-red-600/60 focus:ring-red-500' : 'border-slate-700 focus:ring-indigo-500'}`} />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {formData.apiKey && keyStatus==='valid' && <SafeIcon icon={FiCheckCircle} className="text-green-400 text-sm" />}
                {formData.apiKey && keyStatus && keyStatus !=='valid' && <SafeIcon icon={FiAlertCircle} className="text-red-400 text-sm" />}
                <button type="button" onClick={()=> setShowKey((v)=> !v)} className="text-slate-500 hover:text-slate-300 transition-colors">
                  <SafeIcon icon={showKey ? FiEyeOff : FiEye} />
                </button>
              </div>
            </div>
            {formData.apiKey && keyStatus && keyStatus !=='valid' && (
              <p className="text-xs text-red-400 mt-1 flex items-center gap-1.5">
                <SafeIcon icon={FiAlertCircle} className="shrink-0" />{keyStatus}
              </p>
            )}
            {formData.apiKey && keyStatus==='valid' && <p className="text-xs text-green-400 mt-1">✓ Key format looks correct</p>}
            <p className="text-xs text-slate-600 mt-1">
              Get your key at <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">console.anthropic.com</a> — used only in-browser, never stored.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
              Anthropic Proxy URL <span className="text-slate-600 italic">(optional)</span>
            </label>
            <div className="relative">
              <input type="url" value={proxyUrl} onChange={(e)=> setProxyUrl(e.target.value)} placeholder="https://your-proxy.example.com" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-slate-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all pr-10" />
              <SafeIcon icon={FiServer} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600" />
            </div>
            <p className="text-xs text-slate-600 mt-1">
              Route Anthropic Models and Messages calls through your own server-side proxy. Leave blank for direct browser calls.
            </p>
          </div>

          <button type="button" onClick={()=> setShowTester((v)=> !v)} className="w-full flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg text-xs font-semibold border border-yellow-700/40 text-yellow-500 hover:bg-yellow-950/20 transition-all">
            <SafeIcon icon={FiZap} className="text-xs" />
            {showTester ? 'Hide Model Tester' : '⚡ Test Which Models Work For My Key'}
          </button>
          {showTester && (
            <ModelTester apiKey={formData.apiKey} proxyUrl={proxyUrl} onSelectModel={handleSelectModel} />
          )}
        </div>

        <CanonicalBaseUrlInput value={canonicalBase} onChange={setCanonicalBase} />
        <ScriptUrlInput value={scriptBase} onChange={setScriptBase} productSlug={productSlug} />
        <UrlContextInput value={contextUrl} onChange={setContextUrl} />
        <div className="p-4 bg-slate-950/60 rounded-lg border border-slate-800 space-y-4">
          <h3 className="text-sm font-semibold text-white">Content, research and evidence</h3>
          <label className="block text-xs text-slate-300">Content style
            <select name="contentStyle" value={formData.contentStyle} onChange={handleChange} className="mt-1.5 w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="description">YouTube video description (short, to match the video review)</option>
              <option value="article">Full review article (long)</option>
            </select>
            <span className="block text-slate-500 mt-1">The video description opens with a direct answer on whether the product is worth the money. It speaks from first-hand experience only when you fill in your own evidence below; otherwise it makes no testing claims.</span>
          </label>
          <label className="flex items-start gap-2 text-xs text-slate-300">
            <input type="checkbox" name="researchReviews" checked={formData.researchReviews} onChange={handleChange} className="mt-0.5" />
            <span><strong className="text-white">Research real user reviews (web search).</strong> Before writing, Claude reads the official site (Source URL) and searches Reddit, Trustpilot, G2, YouTube and forums for genuine reviews, then builds a fact sheet. Pages only summarise reviews that were actually found, with links. One research call per batch (web search costs about $0.01 per search, max 8). Web search must be enabled for your organisation in the Anthropic Console.</span>
          </label>
          <p className="text-xs text-slate-400">Your own evidence (optional). Only enter things you actually have. Pages reference this evidence directly; without it, they state that the product wasn't tested hands-on.</p>
          {[['testNotes','What you tested','Describe the campaign setup, dates, steps and what you observed.'],['metricNotes','Measured results and metrics','Actual numbers with their source and date: indexing, follow/nofollow, DA/DR of created pages, before/after comparisons, caveats.'],['imageEvidence','Screenshots and images (one per line: URL | caption)','https://example.com/dashboard.png | Campaign dashboard after setup\nhttps://example.com/pricing.png | Pricing page, Oct 2026'],['videoNotes','Real video moments (one per line: mm:ss | what happens)','02:08 | Campaign creation: target URL and keyword fields are filled in\n05:40 | Exported white-label report is opened'],['docExcerpts','Documentation excerpts','Paste relevant text from the product documentation or help centre.'],['evidenceUrls','Supporting links (one URL per line)','Exported reports, public campaign data, source documents.']].map(([name,label,placeholder]) => (
            <label key={name} className="block text-xs text-slate-300">{label}
              <textarea name={name} value={formData[name]} onChange={handleChange} rows={3} placeholder={placeholder} className="mt-1.5 w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-slate-600 focus:ring-2 focus:ring-indigo-500 outline-none" />
            </label>
          ))}
          <InputField label="Your demo / review video URL" type="url" name="verifiedVideoUrl" value={formData.verifiedVideoUrl} onChange={handleChange} placeholder="https://example.com/my-real-review.mp4" hint="Optional. A real recording you made; it becomes the main video on every page. Add timestamps above to describe what happens in it." />
          <label className="flex items-start gap-2 text-xs text-slate-300">
            <input type="checkbox" name="affiliateDisclosure" checked={formData.affiliateDisclosure} onChange={handleChange} className="mt-0.5" />
            <span>I earn a commission from this product (adds an affiliate disclosure and marks links as sponsored).</span>
          </label>
        </div>
        <ThumbnailUrlInput value={customImageBase} onChange={setCustomImageBase} />
        <VideoUrlInput value={customVideoBase} onChange={setCustomVideoBase} />
        <BulkConfig pageCount={bulkCount} onChange={setBulkCount} provider="anthropic" />

        <div>
          <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
            Base Template (HTML/TXT)<span className="text-indigo-400 ml-1">*</span>
          </label>
          <label onDragOver={(e)=> {e.preventDefault();setDragOver(true);}} onDragLeave={()=> setDragOver(false)} onDrop={handleDrop} className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition-all ${dragOver ? 'border-indigo-500 bg-indigo-950/30' : file ? 'border-green-600/50 bg-green-950/10' : 'border-slate-700 bg-slate-950 hover:border-slate-600'}`}>
            <SafeIcon icon={FiUploadCloud} className={`text-2xl mb-2 ${file ? 'text-green-400' : 'text-slate-500'}`} />
            <p className="text-sm text-slate-400">
              {file ? (
                <span className="text-green-400 font-medium">{file.name}</span>
              ) : (
                <><span className="font-semibold text-indigo-400">Click to upload</span> or drag and drop</>
              )}
            </p>
            <p className="text-xs text-slate-600 mt-1">.txt or .html template file</p>
            <input type="file" className="hidden" accept=".txt,.html" onChange={(e)=> handleFile(e.target.files[0])} />
          </label>
        </div>

        <button disabled={isGenerating} className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm text-white transition-all mt-2 ${isGenerating ? 'bg-indigo-600/40 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-500 shadow-lg shadow-indigo-500/20 active:scale-[0.98]'}`}>
          <SafeIcon icon={FiPackage} className={isGenerating ? 'animate-pulse' : ''} />
          {isGenerating ? 'Building Pages...' : `Generate ${bulkCount} Pages → ZIP`}
        </button>
      </div>
    </form>
  );
};

export default GeneratorForm;